import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox, FormControlLabel } from '@mui/material';
import OvCompactButton from '../atoms/OvCompactButton';
import OvVisuallyHidden from '../atoms/OvVisuallyHidden';
import OvStagedFileNotices from '../molecules/OvStagedFileNotices';
import OvVideoBatchAssignment from '../molecules/OvVideoBatchAssignment';
import OvVideoDropZone from '../molecules/OvVideoDropZone';
import OvRecordList from './OvRecordList';
import { MAX_BULK_VIDEOS } from '../../../common/model/dto/video/video-constants';
import { ProposedAssignment } from '../../../common/model/type/video-staging.types';
import { useStagedVideoColumns } from '../../../hooks/use-staged-video-columns';
import { useVideoStagingSession } from '../../../hooks/use-video-staging-session';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

const OvVideoBulkUploadForm: FC = () => {
  const { t } = useTranslation();
  const session = useVideoStagingSession();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const selected = session.staged.filter(({ key }) =>
    selectedKeys.includes(key)
  );
  const isAllSelected =
    session.staged.length > 0 && selected.length === session.staged.length;
  const isDisabled = session.isSubmitting;

  const toggleSelected = (key: string) =>
    setSelectedKeys((keys) =>
      keys.includes(key) ? keys.filter((item) => item !== key) : [...keys, key]
    );

  const columns = useStagedVideoColumns({
    selectedKeys,
    onToggleSelected: toggleSelected,
  });

  const addFiles = (files: File[]) => {
    const { added } = session.addFiles(files);
    setAnnouncement(
      added ? t('videos.staging.filesAdded', { count: added }) : ''
    );
  };

  const apply = (proposals: ProposedAssignment[]) => {
    proposals.forEach(({ key, values }) => session.setPlacement(key, values));
    setIsAssigning(false);
  };

  return (
    <Form>
      <OvVideoDropZone
        label={t('videos.staging.addFiles')}
        buttonLabel={t('videos.staging.addFiles')}
        hint={t('videos.staging.dropHere')}
        limits={t('videos.staging.limits', { max: MAX_BULK_VIDEOS })}
        isMultiple
        isDisabled={isDisabled}
        onFiles={addFiles}
      />
      <OvVisuallyHidden role="status">{announcement}</OvVisuallyHidden>

      <OvStagedFileNotices
        rejected={session.rejected}
        duplicates={session.duplicates}
      />

      {session.staged.length === 0 ? (
        <Empty>{t('videos.bulk.emptySession')}</Empty>
      ) : (
        <>
          <Toolbar>
            <Count>
              {t('videos.staging.videoCount', {
                count: session.staged.length,
              })}
            </Count>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isAllSelected}
                  indeterminate={selected.length > 0 && !isAllSelected}
                  disabled={isDisabled}
                  onChange={() =>
                    setSelectedKeys(
                      isAllSelected ? [] : session.staged.map(({ key }) => key)
                    )
                  }
                />
              }
              label={t('videos.bulk.selectAll')}
            />
            <Selected>
              {t('videos.bulk.selectedCount', { count: selected.length })}
            </Selected>
            <OvCompactButton
              tone="secondary"
              aria-expanded={isAssigning}
              disabled={!selected.length || isDisabled}
              onClick={() => setIsAssigning(true)}
            >
              {t('videos.bulk.applyToSelected')}
            </OvCompactButton>
          </Toolbar>

          {isAssigning && selected.length > 0 && (
            <OvVideoBatchAssignment
              selected={selected}
              onApply={apply}
              onCancel={() => setIsAssigning(false)}
            />
          )}

          <OvRecordList
            label={t('videos.staging.listLabel')}
            records={session.staged}
            columns={columns}
            getKey={({ key }) => key}
          />
        </>
      )}
    </Form>
  );
};

export default OvVideoBulkUploadForm;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.MEDIUM};
`;

const Empty = styled.p`
  margin: 0;
  color: ${Colours.OV_CHARCOAL};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Variables.spacing.SMALL} ${Variables.spacing.LARGE};
`;

const Count = styled.h2`
  margin: 0;
  font-size: ${Variables.fontSizes.MEDIUM};
`;

const Selected = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;
